import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";

import { Button, Input, XStack } from "@/components";
import ErrorLabel from "@/components/forms/ErrorLabel";
import { PhaseContainer } from "./PhaseContainer";

const zipSchema = z.object({
  zipCode: z
    .string()
    .min(1, "Please enter your zip code")
    .regex(/^\d{5}$/, "Zip code must be 5 digits"),
});

type ZipFormData = z.infer<typeof zipSchema>;

type ZipPhaseProps = {
  initialZipCode: string;
  onSearch: (zipCode: string) => void;
  onBack: () => void;
  providersLoading: boolean;
  isProvidersError: boolean;
};

export const ZipPhase = ({
  initialZipCode,
  onSearch,
  onBack,
  providersLoading,
  isProvidersError,
}: ZipPhaseProps) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<ZipFormData>({
    resolver: zodResolver(zipSchema),
    mode: "onBlur",
    defaultValues: { zipCode: initialZipCode },
  });

  const onSubmit = handleSubmit(({ zipCode }) => onSearch(zipCode));

  return (
    <PhaseContainer
      title="Find a provider near you"
      currentStep={2}
      onBack={onBack}
    >
      <Controller
        control={control}
        name="zipCode"
        render={({ field: { onChange, onBlur, value } }) => (
          <Input.Controlled
            label="Zip code"
            placeholder="Enter zip code"
            value={value}
            onChangeText={onChange}
            onBlur={onBlur}
            inputMode="numeric"
            maxLength={5}
            onSubmitEditing={() => onSubmit()}
            errorText={errors.zipCode?.message}
          />
        )}
      />
      {isProvidersError && (
        <ErrorLabel>Failed to load providers. Please try again.</ErrorLabel>
      )}
      <XStack justifyContent="flex-end">
        <Button mode="primary" onPress={onSubmit} loading={providersLoading}>
          Continue
        </Button>
      </XStack>
    </PhaseContainer>
  );
};
